﻿import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Layers, LayoutGrid, Share2, ScanSearch, Clock, Link2,
  ShieldCheck, Award, Users, Flag,
  ArrowRight, Check, X,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoModal } from "../components/BookDemoModal";
import { Reveal } from "../components/Reveal";
import DashboardCarousel from "../components/DashboardCarousel";
import NeedChat from "../components/NeedChat";

const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── Design tokens ──────────────────────────────────────────────────────────
const G        = "#0D2B22";
const GSOFT    = "#123528";
const GLINE    = "#1E4838";
const CREAM    = "#E8E3D5";
const CREAMLINE= "#D6CFBC";
const INK      = "#1A2E24";
const INKSOFT  = "#3C5447";
const MUTED    = "#9DB3A6";
const TEAL     = "#2EB39A";
const TEALB    = "#38C9AE";
const TEALDIM  = "#1F8C78";

// ── Shared display font ────────────────────────────────────────────────────
const FF_DISP = "'Space Grotesk', system-ui, sans-serif";
const FF_BODY = "'DM Sans', system-ui, sans-serif";

// ── Eyebrow (dark sections) ────────────────────────────────────────────────
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      <span className="block h-px w-6" style={{ backgroundColor: TEAL }} />
      <span style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", color: TEAL }}>
        {children}
      </span>
    </div>
  );
}

// ── Eyebrow (cream sections) ───────────────────────────────────────────────
function EyebrowCreamy({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", color: TEAL, marginBottom: 16 }}>
      {children}
    </p>
  );
}

// ── Feature card (cream) ───────────────────────────────────────────────────
function FeatureCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      className="rounded-2xl p-7 flex flex-col gap-4"
      style={{
        backgroundColor: "#fff",
        border: `1px solid ${hovered ? TEALDIM : CREAMLINE}`,
        boxShadow: hovered ? "0 8px 28px rgba(0,0,0,0.13)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: G }}>
        <div style={{ color: TEAL }}>{icon}</div>
      </div>
      <div>
        <h3 className="mb-1.5" style={{ fontFamily: FF_DISP, fontSize: 17, fontWeight: 600, color: INK }}>{title}</h3>
        <p style={{ fontSize: 14.5, lineHeight: 1.62, color: INKSOFT }}>{desc}</p>
      </div>
    </div>
  );
}


// ── Testimonial card ───────────────────────────────────────────────────────
function TestiCard({ quote, initials, role, sub }: { quote: string; initials: string; role: string; sub: string }) {
  return (
    <div className="rounded-2xl p-8 flex flex-col" style={{ backgroundColor: "#fff", border: `1px solid ${CREAMLINE}` }}>
      <span className="font-bold leading-none mb-4" style={{ fontFamily: FF_DISP, fontSize: 40, color: TEAL }}>"</span>
      <p className="italic flex-1 mb-6" style={{ fontSize: 15, lineHeight: 1.65, color: INK }}>{quote}</p>
      <div className="flex items-center gap-3 pt-5" style={{ borderTop: `1px solid ${CREAMLINE}` }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white shrink-0"
          style={{ backgroundColor: GSOFT }}>
          {initials}
        </div>
        <div>
          <p style={{ fontFamily: FF_DISP, fontSize: 14, fontWeight: 600, color: INK }}>{role}</p>
          <p style={{ fontSize: 13, color: MUTED }}>{sub}</p>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
export default function HomePage() {
  const [demoOpen, setDemoOpen] = useState(false);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ fontFamily: FF_BODY, backgroundColor: G, color: "#fff", overflowX: "hidden" }}>
      <style>{`
        @keyframes tealPulse {
          0%, 100% { box-shadow: 0 0 6px #2EB39A, 0 0 14px rgba(46,179,154,0.35); }
          50% { box-shadow: 0 0 14px #2EB39A, 0 0 28px rgba(46,179,154,0.65); }
        }
        .teal-pulse-dot { animation: tealPulse 2.6s ease-in-out infinite; animation-delay: 0.8s; }
        @keyframes problemWipeLeft  { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0% 0 0); } }
        @keyframes problemWipeRight { from { clip-path: inset(0 0 0 100%); } to { clip-path: inset(0 0% 0 0%); } }
        .problem-wipe-left  { animation: problemWipeLeft  800ms cubic-bezier(0.22,1,0.36,1) both; }
        .problem-wipe-right { animation: problemWipeRight 800ms cubic-bezier(0.22,1,0.36,1) 200ms both; }
        @keyframes truthBreathe { 0%,100% { opacity: 0.2; } 50% { opacity: 1; } }
        .truth-bg-r1 { animation: truthBreathe 6.5s ease-in-out infinite; }
        .truth-bg-r2 { animation: truthBreathe 6.5s ease-in-out infinite reverse; animation-delay: -2.145s; }
        @media (prefers-reduced-motion: reduce) {
          .teal-pulse-dot { animation: none; }
          .problem-wipe-left, .problem-wipe-right { animation: none; clip-path: none; opacity: 1; }
          .truth-bg-r1, .truth-bg-r2 { animation: none; }
        }
        /* ── How It Works — Dark Manifest ── */
        #how { background-color: #ffffff; }
        #how .v1-header { text-align: left; margin-bottom: 64px; }
        #how .v1-eyebrow { display: flex; align-items: center; gap: 8px; margin-bottom: 20px; }
        #how .v1-eyebrow-line { display: block; height: 1px; width: 24px; background: #2EB39A; }
        #how .v1-eyebrow-text { font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: #2EB39A; }
        #how .v1-h2 { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: clamp(32px, 4.2vw, 56px); font-weight: 900; line-height: 1.06; letter-spacing: -0.03em; color: #0D1F1A; margin-bottom: 16px; text-wrap: balance; }
        #how .v1-sub { font-size: 16px; line-height: 1.62; color: #3C5447; max-width: 52ch; }
        #how .v1-rule { width: 100%; height: 1px; background: #D6CFBC; margin-bottom: 0; }
        #how .v1-grid { display: grid; grid-template-columns: 1fr; }
        @media (min-width: 768px) { #how .v1-grid { grid-template-columns: 1fr 1fr; } }
        #how .v1-item {
          display: flex; flex-direction: column; gap: 10px;
          padding: 32px 0; border-bottom: 1px solid #D6CFBC;
          opacity: 0; transform: translateY(22px);
          animation: v1Rise 0.6s cubic-bezier(0.22,1,0.36,1) forwards;
          animation-delay: var(--v1-delay, 0ms);
        }
        @media (prefers-reduced-motion: reduce) { #how .v1-item { opacity: 1; transform: none; animation: none; } }
        #how .v1-item-inner { display: flex; gap: 24px; align-items: flex-start; }
        #how .v1-num { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: clamp(36px, 4vw, 56px); font-weight: 900; line-height: 1; letter-spacing: -0.04em; color: #2EB39A; min-width: 64px; padding-top: 2px; transition: color 0.2s; }
        #how .v1-item:hover .v1-num { color: #38C9AE; }
        #how .v1-icon-well { width: 40px; height: 40px; border-radius: 10px; background: #0D2B22; display: flex; align-items: center; justify-content: center; color: #2EB39A; flex-shrink: 0; margin-top: 4px; }
        #how .v1-body { display: flex; flex-direction: column; gap: 6px; }
        #how .v1-title { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: 17px; font-weight: 700; color: #0D1F1A; letter-spacing: -0.01em; }
        #how .v1-desc { font-size: 14.5px; line-height: 1.62; color: #3C5447; }
        @keyframes v1Rise {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* ── Hero: Architectural Split ── */
        .v2-dots {
          position: absolute; inset: 0; z-index: -1; pointer-events: none; opacity: 0.35;
          background-image: radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1.4px);
          background-size: 28px 28px;
          animation: v2-dots-drift 24s linear infinite;
          mask-image: radial-gradient(ellipse 85% 80% at 50% 30%, #000 20%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse 85% 80% at 50% 30%, #000 20%, transparent 75%);
        }
        @keyframes v2-dots-drift { from { background-position: 0 0; } to { background-position: 28px 28px; } }
        @media (prefers-reduced-motion: reduce) { .v2-dots { animation: none; } }
        .v2-inner {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: clamp(40px,5vw,80px); align-items: center;
          max-width: 1300px; margin: 0 auto; width: 100%;
          padding: clamp(120px,15vh,174px) clamp(24px,4vw,64px) 0;
          position: relative;
        }
        @media (max-width: 900px) {
          .v2-inner { grid-template-columns: 1fr; text-align: center; padding-top: clamp(100px,12vh,130px); }
        }
        .v2-text-col {
          display: flex; flex-direction: column; align-items: flex-start;
          animation: v2-slide-left 0.95s cubic-bezier(0.22,1,0.36,1) both;
        }
        @media (max-width: 900px) {
          .v2-text-col { align-items: center; animation: v2-rise 0.9s cubic-bezier(0.22,1,0.36,1) both; }
        }
        @keyframes v2-slide-left { from { opacity:0; transform: translateX(-36px); } to { opacity:1; transform: none; } }
        @keyframes v2-rise       { from { opacity:0; transform: translateY(24px);   } to { opacity:1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .v2-text-col { animation: none; } }
        .v2-badge {
          display: inline-flex; align-items: center;
          padding: 5px 14px; border-radius: 9999px; margin-bottom: 20px;
          border: 1px solid rgba(46,179,154,0.32); background: rgba(46,179,154,0.08);
        }
        .v2-h1 {
          font-size: clamp(42px,5.5vw,90px); font-weight: 900; line-height: 1.02;
          letter-spacing: -0.03em; margin-bottom: 20px; color: white;
        }
        .v2-teal { color: #2EB39A; text-shadow: 0 0 30px rgba(46,179,154,0.6), 0 0 60px rgba(46,179,154,0.2); }
        .v2-body {
          font-size: clamp(15px,1.3vw,17.5px); line-height: 1.65;
          color: rgba(255,255,255,0.72); max-width: 46ch; margin-bottom: 28px;
        }
        .v2-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
        .v2-btn-primary {
          display: flex; align-items: center; gap: 8px;
          border-radius: 12px; padding: 13px 26px; font-weight: 600; font-size: 15px;
          background: #2EB39A; color: #0D2B22; border: none; cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .v2-btn-primary:hover { background: #3ECFB2; transform: translateY(-2px); }
        .v2-btn-secondary {
          border-radius: 12px; padding: 13px 26px; font-weight: 600; font-size: 15px;
          border: 1px solid rgba(255,255,255,0.2); color: rgba(255,255,255,0.88);
          background: transparent; cursor: pointer; transition: all 0.2s;
        }
        .v2-btn-secondary:hover { border-color: rgba(46,179,154,0.5); background: rgba(46,179,154,0.06); }
        .v2-social { display: flex; align-items: center; gap: 8px; }
        .v2-pulse-dot {
          width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
          background: #2EB39A; box-shadow: 0 0 8px #2EB39A;
        }
        .v2-divider {
          position: absolute; left: calc(50% - 0.5px); top: 18%; bottom: 18%; width: 1px;
          background: linear-gradient(to bottom, transparent, rgba(46,179,154,0.28) 25%, rgba(46,179,154,0.28) 75%, transparent);
        }
        @media (max-width: 900px) { .v2-divider { display: none; } }
        .v2-carousel-col { animation: v2-slide-right 1.05s cubic-bezier(0.22,1,0.36,1) 0.12s both; }
        @keyframes v2-slide-right { from { opacity:0; transform: translateX(36px); } to { opacity:1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .v2-carousel-col { animation: none; } }
        .v2-carousel-inner { transform: perspective(1200px) rotateY(-4deg); transition: transform 0.5s ease; }
        /* ── Who It's For — Asymmetric split ── */
        @keyframes wif-clip-up { from { clip-path: inset(100% 0 0 0); opacity: 0.4; } to { clip-path: inset(0% 0 0 0); opacity: 1; } }
        @keyframes wif-teal-draw { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .v1s-wrap { background: #E8E3D5; padding: clamp(72px,9vw,128px) clamp(16px,4vw,40px); }
        .v1s-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: minmax(0,2fr) minmax(0,3fr); gap: clamp(48px,6vw,96px); align-items: start; }
        @media (max-width: 860px) { .v1s-inner { grid-template-columns: 1fr; } .v1s-left { position: static !important; } }
        .v1s-left { position: sticky; top: 88px; }
        .v1s-eyebrow { display: inline-block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: #2EB39A; margin-bottom: 20px; }
        .v1s-h2 { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: clamp(30px,3.8vw,52px); font-weight: 900; line-height: 1.06; letter-spacing: -0.03em; color: #1A2E24; margin-bottom: 20px; text-wrap: balance; position: relative; padding-bottom: 24px; }
        .v1s-h2::after { content: ''; position: absolute; bottom: 0; left: 0; width: 48px; height: 3px; background: #2EB39A; border-radius: 2px; transform-origin: left; animation: wif-teal-draw 0.55s cubic-bezier(0.22,1,0.36,1) 0.3s both; }
        .v1s-body { font-size: 15px; line-height: 1.7; color: #3C5447; }
        .v1s-body strong { color: #1A2E24; font-weight: 700; }
        .v1s-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .v1s-card { background: #fff; border: 1px solid #D6CFBC; border-radius: 18px; padding: clamp(18px,2vw,26px); display: flex; flex-direction: column; gap: 14px; transition: box-shadow 0.25s cubic-bezier(0.22,1,0.36,1), border-color 0.25s, transform 0.25s cubic-bezier(0.22,1,0.36,1); animation: wif-clip-up 0.6s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d, 0ms); }
        .v1s-card:hover { border-color: #2EB39A; box-shadow: 0 12px 36px rgba(0,0,0,0.11), 0 2px 8px rgba(46,179,154,0.12); transform: translateY(-4px); }
        .v1s-icon { width: 48px; height: 48px; border-radius: 13px; flex-shrink: 0; background: linear-gradient(145deg, #0D2B22, #1A4535); display: flex; align-items: center; justify-content: center; color: #2EB39A; }
        .v1s-card h3 { font-family: 'Space Grotesk', system-ui, sans-serif; font-size: 15px; font-weight: 700; color: #1A2E24; margin: 0 0 5px; }
        .v1s-card p { font-size: 13.5px; line-height: 1.62; color: #3C5447; margin: 0; }
        @media (prefers-reduced-motion: reduce) { .v1s-card { animation: none !important; } .v1s-h2::after { animation: none !important; transform: scaleX(1); } }

        /* ── CTA section ── */
        .cta-section { position: relative; overflow: hidden; }
        .cta-section::before {
          content: "";
          position: absolute;
          top: -120px;
          left: 50%;
          transform: translateX(-50%);
          width: 600px;
          height: 400px;
          background: radial-gradient(ellipse at 50% 0%, rgba(46,179,154,0.11) 0%, transparent 72%);
          pointer-events: none;
        }
        .v3-accent-line { width: 48px; height: 3px; background: #2EB39A; border-radius: 2px; margin: 0 auto 1.5rem; opacity: 0.75; }
        .v3-heading { font-family: "Space Grotesk", system-ui, sans-serif; font-size: clamp(30px, 4.2vw, 56px); font-weight: 900; line-height: 1.08; letter-spacing: -0.02em; color: #fff; margin-bottom: 1.25rem; text-wrap: balance; }
        .v3-sub { font-size: 16px; color: rgba(255,255,255,.58); line-height: 1.65; max-width: 62ch; margin: 0 auto 2.25rem; }
        .v3-btn { display: inline-block; background: linear-gradient(135deg, #2EB39A, #1e9e86); color: #0D2B22; font-size: 16px; font-weight: 700; padding: 1rem 2.75rem; border-radius: 12px; border: none; cursor: pointer; transition: opacity 0.18s; box-shadow: 0 4px 24px rgba(46,179,154,0.2); margin-bottom: 0.875rem; }
        .v3-btn:hover { opacity: 0.9; }
        .v3-micro { font-size: 13px; color: rgba(255,255,255,.35); margin: 0 auto 1.75rem; max-width: 50ch; }
        .v3-badges { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 0.625rem; }
        .v3-badge-filled { display: flex; align-items: center; gap: 0.5rem; padding: 0.375rem 1rem; border-radius: 999px; background: rgba(46,179,154,0.1); border: 1px solid rgba(46,179,154,0.5); font-size: 12.5px; color: rgba(255,255,255,0.7); }
      `}</style>
      <Navbar />

      {/* ══════════════════════════════════════════════════════════════
          1. HERO
          ══════════════════════════════════════════════════════════════ */}
          <section
            id="top"
            style={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden", isolation: "isolate", background: G, paddingBottom: "60px" }}
          >
            <div className="v2-dots" />
            <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: -2, pointerEvents: "none", background: `radial-gradient(78% 52% at 50% -10%, rgba(232,227,213,.07), transparent 62%), radial-gradient(72% 58% at 50% 98%, rgba(30,72,56,.35), transparent 60%), linear-gradient(180deg, #0A2018 0%, ${G} 40%, #0A211A 100%)` }} />

            <div className="v2-inner">
              <div className="v2-text-col">
                    <div className="v2-badge">
                      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.14em", color: TEAL }}>Purpose-built for compliance consultants</span>
                    </div>
                    <h1 className="v2-h1" style={{ fontFamily: FF_DISP }}>
                      <span className="block">Your practice.</span>
                      <span className="block">
                        <span className="v2-teal">Governed.</span>
                        <span> At scale.</span>
                      </span>
                    </h1>
                    <p className="v2-body">
                      Revisr gives compliance consultants the infrastructure to encode their methodology, manage every client in one governed workspace, and deliver audit-ready outcomes — without scaling their team.
                    </p>
                    <div className="v2-ctas">
                      <button type="button" className="v2-btn-primary" onClick={() => setDemoOpen(true)}>
                        Book a demo <ArrowRight className="w-4 h-4" />
                      </button>
                      <button type="button" className="v2-btn-secondary" onClick={() => scrollTo("how")}>
                        See how it works
                      </button>
                    </div>
                    <div className="v2-social">
                      <span className="v2-pulse-dot" />
                      <span style={{ fontSize: 13, color: MUTED }}>No commitments. 30-minute call.</span>
                    </div>
                  </div>

              <div className="v2-divider" />

              <div className="v2-carousel-col">
                <div className="v2-carousel-inner">
                  <DashboardCarousel
                    autoPlayMs={6000}
                    slides={[
                      { src: "/image1.png", alt: "QHSE compliance dashboard — ports & shipping", label: "Ports & shipping" },
                      { src: "/image2.png", alt: "QHSE compliance dashboard — warehousing & logistics", label: "Warehousing & logistics" },
                      { src: "/image3.png", alt: "QHSE compliance dashboard — construction site", label: "Construction site" },
                      { src: "/image4.png", alt: "QHSE compliance dashboard — manufacturing", label: "Manufacturing" },
                      { src: "/image5.png", alt: "QHSE compliance dashboard — mining & resources", label: "Mining & resources" },
                      { src: "/image6.png", alt: "QHSE compliance dashboard — oil, gas and energy", label: "Oil, gas and energy" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </section>

      {/* ══════════════════════════════════════════════════════════════
          3. TRUTH STRIP (cream) — matches HTML .truth
      ══════════════════════════════════════════════════════════════ */}
      <section style={{ backgroundColor: CREAM, color: INK, textAlign: "center", paddingBlock: "clamp(80px,12vw,150px)", paddingInline: "clamp(24px,6vw,48px)", position: "relative", overflow: "hidden" }}>
        <div aria-hidden className="truth-bg-r1" style={{ position: "absolute", inset: 0, background: "radial-gradient(65% 55% at 50% 50%, rgba(46,179,154,0.055), transparent)", pointerEvents: "none" }} />
        <div aria-hidden className="truth-bg-r2" style={{ position: "absolute", inset: 0, background: "radial-gradient(80% 60% at 50% 110%, rgba(46,179,154,0.035), transparent)", pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", top: "clamp(10px,3vw,40px)", left: "50%", transform: "translateX(-50%)", fontFamily: FF_DISP, fontSize: "clamp(120px,14vw,200px)", fontWeight: 900, lineHeight: 1, color: "rgba(26,46,36,0.055)", userSelect: "none" as React.CSSProperties["userSelect"], pointerEvents: "none", letterSpacing: "-0.05em", display: "block" }}>"</div>
        <Reveal style={{ position: "relative", maxWidth: 660, margin: "0 auto" }}>
          <p style={{ fontFamily: FF_DISP, fontWeight: 400, fontSize: "clamp(16px,1.6vw,20px)", lineHeight: 1.75, color: INKSOFT, marginBottom: "clamp(20px,2.5vw,32px)" }}>Generating a document was never the hard part.</p>
          <blockquote style={{ fontFamily: FF_DISP, fontWeight: 700, fontSize: "clamp(26px,3.3vw,46px)", lineHeight: 1.3, letterSpacing: "-0.025em", color: INK, maxWidth: "19ch", margin: "0 auto clamp(28px,3.5vw,44px)", textWrap: "balance" as React.CSSProperties["textWrap"] }}>
            Proving{" "}
            <em style={{ fontStyle: "normal", color: TEALDIM }}>who approved it</em>, which standard it maps to, and whether the business can{" "}
            <em style={{ fontStyle: "normal", color: TEALDIM }}>defend it in audit</em> — that's the hard part.
          </blockquote>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: TEAL, display: "inline-block", boxShadow: `0 0 8px ${TEAL}` }} />
            <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.1em", color: INKSOFT, textTransform: "uppercase" as React.CSSProperties["textTransform"] }}>Revisr — compliance infrastructure</span>
          </div>
        </Reveal>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          4. THE PROBLEM (dark) — id="problem"
      ══════════════════════════════════════════════════════════════ */}
      <section id="problem" className="py-24 sm:py-32 px-4" style={{ backgroundColor: "#0D2B22" }}>
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[clamp(40px,6vw,90px)] items-start">
            <div className="problem-wipe-left">
              <div className="flex items-center gap-2 mb-5">
                <span className="block h-px w-6" style={{ backgroundColor: "#2EB39A" }} />
                <span style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", color: "#2EB39A" }}>The Problem</span>
              </div>
              <h2 style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: "clamp(34px,4vw,54px)", fontWeight: 900, lineHeight: 1.05, letterSpacing: "-0.03em", maxWidth: "14ch", margin: "0 0 24px", color: "#fff" }}>
                Every compliance tool is built for your clients. Not for you.
              </h2>
              <p className="mb-[38px]" style={{ fontSize: 17, lineHeight: 1.62, color: "rgba(255,255,255,.78)", maxWidth: "46ch" }}>
                The entire tech market is racing to make your clients self-sufficient — their AI roadmap:{" "}
                <strong style={{ color: "#fff", fontWeight: 600 }}>remove the consultant</strong>. It doesn't understand the value you bring. We do.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {[
                  { title: "25% of your time goes to formatting",  sub: "Not to the advisory work clients actually pay for." },
                  { title: "Your methodology lives in your head",   sub: "Every new client means rebuilding the same frameworks." },
                  { title: "AI tools are making governance worse",  sub: "Clients use ChatGPT with zero oversight or approval trails." },
                ].map(({ title, sub }) => (
                  <div key={title} style={{ paddingLeft: 16, borderLeft: "2px solid #2EB39A" }}>
                    <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 3 }}>{title}</p>
                    <p style={{ fontSize: 13.5, color: "#9DB3A6", lineHeight: 1.5 }}>{sub}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="problem-wipe-right grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl p-6 h-full" style={{ backgroundColor: "#123528", border: "1px solid #1E4838" }}>
                <p className="mb-5" style={{ fontSize: 13, color: "rgba(255,255,255,.42)", fontWeight: 500 }}>The rest of the market</p>
                <div className="flex flex-col gap-3.5">
                  {["Sells directly to your clients","Automates what you charge for","Generates documents, not governed systems","Disintermediates the consultant","AI roadmap = remove the human"].map(item => (
                    <div key={item} className="flex items-start gap-2.5">
                      <X className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "rgba(255,255,255,.3)" }} />
                      <p style={{ fontSize: 13.5, color: "rgba(255,255,255,.45)" }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-6 h-full" style={{ backgroundColor: "#123528", border: "1px solid rgba(46,179,154,0.4)", boxShadow: "inset 0 0 0 1px rgba(46,179,154,0.1)" }}>
                <div className="flex items-center gap-2 mb-5">
                  <img src="/revisrlogo3.png" alt="Revisr" style={{ height: 28, width: "auto" }} />
                </div>
                <div className="flex flex-col gap-3.5">
                  {["You are the primary customer","Encode your methodology once","Governed delivery, full audit trail","Scale from 10 clients to 40, same team","AI that amplifies, never replaces"].map(item => (
                    <div key={item} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#2EB39A" }} />
                      <p style={{ fontSize: 13.5, fontWeight: 500, color: "#fff" }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          5. HOW IT WORKS (cream) — id="how"
      ══════════════════════════════════════════════════════════════ */}
      <section id="how" className="px-4" style={{ paddingTop: "clamp(72px,8vw,120px)", paddingBottom: "clamp(72px,8vw,120px)" }}>
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
          <div className="v1-header">
            <div className="v1-eyebrow"><span className="v1-eyebrow-line" /><span className="v1-eyebrow-text">How It Works</span></div>
            <h2 className="v1-h2">Practice infrastructure.<br />Not a drafting tool.</h2>
            <p className="v1-sub">Revisr gives you the operating layer compliance consultants have always needed but never had.</p>
          </div>
          </Reveal>
          <div className="v1-rule" />
          <div className="v1-grid">
            {([
              { Icon: Layers,     num: "01", title: "Encode your methodology",       desc: "Build your frameworks and standards interpretation once. Deploy across every client." },
              { Icon: LayoutGrid, num: "02", title: "Multi-client workspaces",       desc: "One dashboard, every client. Isolated data, controlled access, full audit trail." },
              { Icon: Share2,     num: "03", title: "Governed approval chains",      desc: "Every change tracked: who proposed, which standard, who approved, who was trained." },
              { Icon: ScanSearch, num: "04", title: "AI-assisted gap analysis",      desc: "AI scans, flags gaps, suggests rewrites. You approve. AI suggests — the system governs." },
              { Icon: Clock,      num: "05", title: "Compliance status at a glance", desc: "Audit readiness, outstanding approvals, flagged issues — all clients, one view." },
              { Icon: Link2,      num: "06", title: "Standards-to-evidence linkage", desc: "Link obligations to documents to approvals to training records to evidence. The loop closes." },
            ] as const).map(({ Icon, num, title, desc }, i) => (
              <div key={title} className="v1-item" style={{ "--v1-delay": `${i * 80}ms` } as React.CSSProperties}>
                <div className="v1-item-inner">
                  <span className="v1-num">{num}</span>
                  <div className="v1-body">
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div className="v1-icon-well"><Icon className="w-5 h-5" /></div>
                      <span className="v1-title">{title}</span>
                    </div>
                    <p className="v1-desc">{desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          6. STATS (dark)
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4" style={{ backgroundColor: G }}>
          <div className="max-w-[1200px] mx-auto">
            <Reveal>
              <div className="grid grid-cols-2 lg:grid-cols-4">
                {[
                  { num: "25%",  lab: "of consultant time lost to formatting and version control" },
                  { num: "900+", lab: "compliance consulting firms in Australia's industrial sector" },
                  { num: "×4",   lab: "client capacity with a governed practice platform" },
                  { num: "0",    lab: "competitors building for consultants, not against them" },
                ].map(({ num, lab }, i) => (
                  <div key={num} style={{ padding: `14px clamp(16px,3vw,38px)`, borderLeft: i === 0 ? "none" : `1px solid ${GLINE}` }}>
                    <p style={{ fontFamily: FF_DISP, fontWeight: 700, fontSize: "clamp(44px,5.4vw,72px)", color: TEAL, letterSpacing: "-0.03em", lineHeight: 1 }}>{num}</p>
                    <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.45, marginTop: 14, maxWidth: "22ch" }}>{lab}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

      {/* ══════════════════════════════════════════════════════════════
          7. WHO IT'S FOR (cream)
      ══════════════════════════════════════════════════════════════ */}
          <div className="v1s-wrap">
            <div className="v1s-inner">
              <div className="v1s-left">
                <span className="v1s-eyebrow">Who It's For</span>
                <h2 className="v1s-h2">Built for consultants who want to advise, not administer.</h2>
                <p className="v1s-body">If you're billing <strong>$15K–$60K per client per year</strong> and spending a quarter of that time on reformatting, version control, and email review loops — Revisr is for you.</p>
              </div>
              <div className="v1s-grid">
                {([
                  { Icon: ShieldCheck, title: "QHSE Consultants",       desc: "ISO 9001, 14001, 45001. Scale the practice, not the admin.", d: 0 },
                  { Icon: Award,       title: "ISO Compliance Advisors", desc: "Certify more clients without more junior staff.",           d: 100 },
                  { Icon: Users,       title: "Operations Consultants",  desc: "Turn one-off engagements into governed systems.",           d: 200 },
                  { Icon: Flag,        title: "Safety Practitioners",    desc: "WHS, SWMS, safety management at scale.",                   d: 300 },
                ] as { Icon: React.ComponentType<{ className: string }>; title: string; desc: string; d: number }[]).map(({ Icon, title, desc, d }) => (
                  <div key={title} className="v1s-card" style={{ "--d": `${d}ms` } as React.CSSProperties}>
                    <div className="v1s-icon"><Icon className="w-5 h-5" /></div>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          9. CTA (dark)
      â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {/* ─────────────────────────────────────────────────────────────────
          9. CTA (dark)
      ───────────────────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-32 px-4 text-center cta-section" style={{ backgroundColor: '#071510', borderTop: '1px solid #1E4838' }}>
        <div className="max-w-[660px] mx-auto" style={{ position: 'relative', zIndex: 1 }}>
          <Reveal>
            <div className="v3-accent-line" aria-hidden="true" />
            <h2 className="v3-heading">
              Stop being a faster version of the same model.{' '}
              <span style={{ color: '#2EB39A' }}>Start building leverage.</span>
            </h2>
            <p className="v3-sub">
              Join our early access cohort. We're working with a small group of compliance consultants to build the platform around real practice needs.
            </p>
            <div>
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="v3-btn"
              >
                Book a demo
              </button>
            </div>
            <p className="v3-micro">
              No commitments. 30-minute call. We'll show you how it works in your practice context.
            </p>
            <div className="v3-badges">
              {[
                { Icon: Flag, label: 'Australian-built' },
                { Icon: ShieldCheck, label: 'ISO / QHSE specialist' },
              ].map(({ Icon, label }) => (
                <div key={label} className="v3-badge-filled">
                  <Icon className="w-3.5 h-3.5" style={{ color: '#2EB39A' }} />
                  {label}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      {/* <NeedChat /> */}
      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}